import { observable, configure, action, computed, toJS } from 'mobx';
import LocalModel from './localModel';
import util from 'preact-util';

configure({ enforceActions: 'always' });

class BlockedUserStore extends LocalModel {
    constructor() {
        super('blockedUser', {
            namePlural: 'blockedUsers',
            sort: '-createdDate',
            limit: 50,
            api: {
                search: {
                    url: '/api/blockedusers/',
                    params: {
                        limit: 50,
                    },
                },
                load: {
                    url: '/api/blockedusers/',
                    params: {},
                },
                save: {
                    url: '/api/blockedusers/',
                    params: {},
                },
                delete: {
                    url: '/api/blockedusers/',
                    params: {},
                },
            },
        });
    }

    @observable blockedUsers = [];

    @observable blockedUser = {};

    @observable myBlockedFeatures = [];

    @observable isBlockedFromAny = false;

    /**
     * Check if the current user is blocked from any features
     * Call this on app load/login
     */
    @action
    async checkMyBlockStatus() {
        try {
            const response = await util.fetchApi('/api/blockedusers/check/me', { method: 'GET' });
            if (response.status === 200 && response.data) {
                this.updateKeyValue('myBlockedFeatures', response.data.blockedFeatures || []);
                this.updateKeyValue('isBlockedFromAny', response.data.isBlocked || false);
                return response.data;
            }
        } catch (err) {
            console.error('Error checking block status:', err);
        }
        return { blockedFeatures: [], isBlocked: false };
    }

    /**
     * Check if user is blocked from a specific feature
     * @param {string} feature - 'supportTickets', 'articles', 'comments', or 'all'
     * @returns {boolean}
     */
    @computed
    get isBlockedFromSupportTickets() {
        return this.myBlockedFeatures.includes('supportTickets') || this.myBlockedFeatures.includes('all');
    }

    @computed
    get isBlockedFromArticles() {
        return this.myBlockedFeatures.includes('articles') || this.myBlockedFeatures.includes('all');
    }

    @computed
    get isBlockedFromComments() {
        return this.myBlockedFeatures.includes('comments') || this.myBlockedFeatures.includes('all');
    }

    /**
     * Get list of blocked users (admin/staff only)
     */
    @action
    async getBlockedUsers(opt = {}) {
        const params = {
            limit: opt.limit || 50,
            offset: opt.offset || 0,
            showUnblocked: opt.showUnblocked || false,
        };

        if (opt.customer) {
            params.customer = opt.customer;
        }
        if (opt.search) {
            params.search = opt.search;
        }

        const response = await util.fetchApi('/api/blockedusers/', { method: 'GET' }, params);
        if (response.status === 200 && response.data) {
            this.updateKeyValue('blockedUsers', response.data);
            return response;
        }
        return response;
    }

    /**
     * Get a single blocked user record
     */
    @action
    async getBlockedUser(uuidv4) {
        const response = await util.fetchApi(`/api/blockedusers/${uuidv4}`, { method: 'GET' });
        if (response.status === 200 && response.data) {
            this.updateKeyValue('blockedUser', response.data);
            return response;
        }
        return response;
    }

    /**
     * Block a user
     */
    @action
    async blockUser({ user, customer, blockedFeatures, reason, internalNotes, startDate, endDate }) {
        const body = {
            user,
            customer,
            blockedFeatures: blockedFeatures || ['all'],
            reason,
            internalNotes,
            startDate,
            endDate,
        };

        const response = await util.fetchApi('/api/blockedusers/', { method: 'POST' }, body);
        if (response.status === 201 && response.data) {
            // Add to blockedUsers list
            const currentBlocked = toJS(this.blockedUsers);
            this.updateKeyValue('blockedUsers', [response.data, ...currentBlocked]);
        }
        return response;
    }

    /**
     * Update a blocked user record
     */
    @action
    async updateBlockedUser(uuidv4, updates) {
        const response = await util.fetchApi(`/api/blockedusers/${uuidv4}`, { method: 'PATCH' }, updates);
        if (response.status === 200 && response.data) {
            // Update in blockedUsers list
            const currentBlocked = toJS(this.blockedUsers);
            const index = currentBlocked.findIndex(b => b.uuidv4 === uuidv4);
            if (index >= 0) {
                currentBlocked[index] = response.data;
                this.updateKeyValue('blockedUsers', currentBlocked);
            }
            this.updateKeyValue('blockedUser', response.data);
        }
        return response;
    }

    /**
     * Unblock a user
     */
    @action
    async unblockUser(uuidv4, reason) {
        const response = await util.fetchApi(`/api/blockedusers/${uuidv4}/unblock`, { method: 'POST' }, { reason });
        if (response.status === 200 && response.data) {
            // Remove from blockedUsers list (or update it)
            const currentBlocked = toJS(this.blockedUsers);
            const index = currentBlocked.findIndex(b => b.uuidv4 === uuidv4);
            if (index >= 0) {
                currentBlocked[index] = response.data;
                this.updateKeyValue('blockedUsers', currentBlocked);
            }
        }
        return response;
    }

    /**
     * Clear blocked user data
     */
    @action
    clearBlockedUser() {
        this.updateKeyValue('blockedUser', {});
    }
}

const blockedUserStore = new BlockedUserStore();

export default blockedUserStore;
